import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { useSelector } from "react-redux";
import { useTheme } from "styled-components";
import TopBanner from "components/atoms/TopBanner";
import { CarrierOutageBannerColor } from "models/carrierOutageBanner/carrierOutageBanner.model";
import {
  fetchCarrierOutageBanners,
  hideCarrierOutageBanner,
} from "services/api/carrierOutageBanner";
import { RootState } from "store";

// Checked every 30 seconds while the tab is visible, and again on coming back
// to it, so a banner ITD switches on (or off) shows without a refresh.
export const CARRIER_OUTAGE_BANNERS_POLL_MS = 30 * 1000;

const CHANGED_EVENT = "connexx:carrier-outage-banners-changed";

type Banner = { id: number; text: string; color: CarrierOutageBannerColor };

// A banner closed here stays hidden even if a check that started before the
// server heard about it comes back with it. Keyed on the words and colour too,
// so it shows again when ITD changes them (the server shows it again then).
const hiddenKey = (banner: Banner) =>
  `${banner.id}|${banner.color}|${banner.text}`;

/** Lets the settings page show a saved change straight away. */
export const announceCarrierOutageBannersChanged = () => {
  window.dispatchEvent(new Event(CHANGED_EVENT));
};

/** The orange carrier outage banners ITD has listed this account on. */
export const CarrierOutageBanners = () => {
  const { accessToken, salesAccountUUID } = useSelector(
    (state: RootState) => state.authSlice
  );
  const theme = useTheme();
  const [banners, setBanners] = useState<Banner[]>([]);
  const [hidden, setHidden] = useState<Set<string>>(new Set());

  useEffect(() => {
    // A different account may not be on the list: never carry banners over.
    setBanners([]);
    setHidden(new Set());
    if (!accessToken || !salesAccountUUID) return;

    let cancelled = false;
    const load = async () => {
      if (document.visibilityState === "hidden") return;
      const { data, error } = await fetchCarrierOutageBanners(salesAccountUUID);
      // One failed check keeps whatever is showing rather than hiding it.
      if (!cancelled && !error) setBanners(data?.banners ?? []);
    };

    load();
    const poll = setInterval(load, CARRIER_OUTAGE_BANNERS_POLL_MS);
    document.addEventListener("visibilitychange", load);
    window.addEventListener(CHANGED_EVENT, load);
    return () => {
      cancelled = true;
      clearInterval(poll);
      document.removeEventListener("visibilitychange", load);
      window.removeEventListener(CHANGED_EVENT, load);
    };
  }, [accessToken, salesAccountUUID]);

  // The X hides the banner for everyone on this account.
  const hide = async (banner: Banner) => {
    if (!salesAccountUUID) return;
    const key = hiddenKey(banner);
    setHidden((current) => new Set(current).add(key));
    const { error } = await hideCarrierOutageBanner(
      salesAccountUUID,
      banner.id
    );
    if (error) {
      setHidden((current) => {
        const next = new Set(current);
        next.delete(key);
        return next;
      });
      toast.error("We couldn't hide this banner. Please try again.");
    }
  };

  // A thin line between stacked banners keeps two messages from reading as
  // one, in the lower banner's own colour.
  return (
    <>
      {banners
        .filter((banner) => !hidden.has(hiddenKey(banner)))
        .map((banner, index) => (
          <div
            key={banner.id}
            style={
              index > 0
                ? { borderTop: `1px solid ${theme.colors[banner.color][8]}` }
                : undefined
            }
          >
            <TopBanner
              variant="carrierOutage"
              text={banner.text}
              color={banner.color}
              onClose={() => hide(banner)}
            />
          </div>
        ))}
    </>
  );
};
